import { useDataProvider, DataProvider } from 'react-admin';
import {
    useQuery,
    useQueryClient,
    UseQueryOptions,
    UseQueryResult,
} from 'react-query';

import {
    GetRootNodesParams,
    GetRootNodesResult,
    TreeDataProvider,
    TreeRecord,
} from '../types';

/**
 * Call the dataProvider.getRootNodes() method and return the resolved value
 * as well as the loading state.
 *
 * The return value updates according to the request state:
 *
 * - start: { isLoading: true, refetch }
 * - success: { data: [data from store], total: [total from response], isLoading: false, refetch }
 * - error: { error: [error from response], isLoading: false, refetch }
 *
 * This hook will return the cached result when called a second time
 * with the same parameters, until the response arrives.
 *
 * @param {string} resource The resource name, e.g. 'posts'
 * @param {Params} params The getTree parameters { meta }
 * @param {Object} options Options object to pass to the queryClient.
 * May include side effects to be executed upon success or failure, e.g. { onSuccess: () => { refresh(); } }
 *
 * @typedef Params
 * @prop params.meta Optional meta parameters
 *
 * @returns The current request state. Destructure as { data, error, isLoading, isFetching }.
 *
 * @example // usage
 *
 * import { useGetRootNodes } from '@react-admin/ra-tree';
 *
 * const Categories = () => {
 *     const { data: tree, isLoading, error } = useGetRootNodes('categories');
 *     if (isLoading) { return <Loading />; }
 *     if (error) { return <p>ERROR</p>; }
 *     return <Tree tree={data} />;
 * };
 */
export const useGetRootNodes = <RecordType extends TreeRecord = TreeRecord>(
    resource: string,
    params: GetRootNodesParams = {},
    options?: UseQueryOptions<GetRootNodesResult<RecordType>['data'], Error>
): UseGetRootNodesHookValue<RecordType> => {
    const dataProvider = useDataProvider<
        TreeDataProvider & DataProvider<string>
    >();
    const queryClient = useQueryClient();
    const { meta } = params;
    const result = useQuery<
        GetRootNodesResult<RecordType>['data'],
        Error,
        GetRootNodesResult<RecordType>['data']
    >(
        [resource, 'getRootNodes', params],
        () =>
            dataProvider
                .getRootNodes<RecordType>(resource, params)
                .then(({ data }) => data),
        {
            onSuccess: data => {
                // optimistically populate the getParentNode cache
                data.forEach(record => {
                    if (record.children) {
                        record.children.forEach(childId => {
                            queryClient.setQueryData(
                                [resource, 'getParentNode', { childId, meta }],
                                oldRecord => oldRecord ?? record
                            );
                        });
                    }
                });
            },
            ...options,
        }
    );

    return result as UseGetRootNodesHookValue<RecordType>;
};

export type UseGetRootNodesHookValue<
    RecordType extends TreeRecord = TreeRecord
> = UseQueryResult<RecordType[], Error> & {
    data?: RecordType[];
};
